/**
 * Hook for global search state and data fetching.
 * Combines client-side navigation search (modules + pages) with debounced
 * server-side entity search.
 */
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from './useDebounce';
import {
  searchApi,
  SearchEntityResult,
  SearchFacet,
  SearchFilters,
  SearchResponse,
  SearchTagResult,
} from '../services/searchApi';
import {
  getSuggestedPages,
  isNavFacet,
  searchNavigation,
  NavSearchResult,
  NAV_FACET_MODULES,
  NAV_FACET_PAGES,
} from '../components/global-search/searchConfig';
import { useModule } from '../contexts/ModuleContext';
import { useNavItemAccess } from './useNavItemAccess';

export const searchKeys = {
  all: ['global-search'] as const,
  query: (q: string, filters: SearchFilters) =>
    [...searchKeys.all, q, filters] as const,
};

interface UseGlobalSearchOptions {
  defaultModule?: string;
}

interface UseGlobalSearchReturn {
  query: string;
  setQuery: (q: string) => void;
  filters: SearchFilters;
  setFilters: React.Dispatch<React.SetStateAction<SearchFilters>>;
  moduleResults: NavSearchResult[];
  pageResults: NavSearchResult[];
  suggestedPages: NavSearchResult[];
  /** Entity matches to render — empty while a navigation category is selected. */
  entities: SearchEntityResult[];
  /** Tag matches to render — gated with the entities they lead to. */
  tags: SearchTagResult[];
  /** "Did you mean…" matches to render — gated with the entities. */
  suggestions: SearchEntityResult[];
  /** Chip row: the two navigation categories, then the server's entity types. */
  facets: SearchFacet[];
  entityQuery: ReturnType<typeof useQuery<SearchResponse>>;
  /** Headline count of everything the results list shows. */
  resultCount: number;
  isLoading: boolean;
}

/** Lucide icon names for the two navigation chips (resolved via `iconMap`). */
const NAV_FACET_ICONS = { modules: 'LayoutGrid', pages: 'FileText' } as const;

// Stable empties for the gated-off sections. A fresh `[]` per render would
// change identity every time and re-run every consumer memo keyed on these
// arrays — including the containers' flat keyboard-navigation list.
const NO_NAV_RESULTS: NavSearchResult[] = [];
const NO_ENTITIES: SearchEntityResult[] = [];
const NO_TAGS: SearchTagResult[] = [];

export function useGlobalSearch(options?: UseGlobalSearchOptions): UseGlobalSearchReturn {
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({
    modules: options?.defaultModule ? [options.defaultModule] : ['all'],
    entityTypes: [],
    dateRange: null,
  });

  const debouncedQuery = useDebounce(query, 300);
  // Nav-item-level access (admin/sysadmin, connected accounts, forwarder
  // persona) — the same gate the sidebar applies, so search never lists a page
  // the sidebar hides. Also carries the persona flag used for module naming.
  const access = useNavItemAccess();

  // Navigation results are drawn from exactly what the module picker offers —
  // the modules this user can actually open. Disabled, above-ceiling,
  // role-gated and hidden modules never surface a module or page result.
  const { pickerModules, pinnedModules } = useModule();
  const navigableModules = useMemo(
    () => [...pickerModules, ...pinnedModules],
    [pickerModules, pinnedModules],
  );

  // Client-side module + page search (instant)
  const { modules: moduleMatches, pages: pageMatches } = useMemo(
    () => searchNavigation(navigableModules, query, filters.modules, access),
    [navigableModules, query, filters.modules, access],
  );

  // Empty-query state: a handful of pages to get started with.
  const suggestedPages = useMemo(
    () => getSuggestedPages(navigableModules, filters.modules, access),
    [navigableModules, filters.modules, access],
  );

  // Picking a category narrows the results to it and nothing else — choosing
  // "Users" must not leave a wall of pages underneath. With nothing selected
  // every section shows, which is the resting state.
  const hasSelection = filters.entityTypes.length > 0;
  const showModules = !hasSelection || filters.entityTypes.includes(NAV_FACET_MODULES);
  const showPages = !hasSelection || filters.entityTypes.includes(NAV_FACET_PAGES);
  const selectedEntityTypes = useMemo(
    () => filters.entityTypes.filter((type) => !isNavFacet(type)),
    [filters.entityTypes],
  );
  const showEntities = !hasSelection || selectedEntityTypes.length > 0;

  // The server never sees the navigation sentinels. Sending only the real types
  // also means a navigation-only selection reuses the unfiltered query's cache
  // entry, so the chip counts stay live instead of blanking on every toggle.
  const serverFilters = useMemo<SearchFilters>(
    () => ({ ...filters, entityTypes: selectedEntityTypes }),
    [filters, selectedEntityTypes],
  );

  // Server-side entity search (debounced)
  const entityQuery = useQuery({
    queryKey: searchKeys.query(debouncedQuery, serverFilters),
    queryFn: () => searchApi.search(debouncedQuery, serverFilters),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
    placeholderData: (prev: SearchResponse | undefined) => prev,
  });

  // Only the entity half of the list is ever pending. While a navigation
  // category is selected the list is already complete, so the spinner would sit
  // under a finished set of pages waiting for rows nobody is going to see.
  const isLoading =
    showEntities &&
    debouncedQuery.length >= 2 &&
    (entityQuery.isLoading || entityQuery.isFetching);

  const moduleResults = showModules ? moduleMatches : NO_NAV_RESULTS;
  const pageResults = showPages ? pageMatches : NO_NAV_RESULTS;
  const entities = showEntities ? entityQuery.data?.entities ?? NO_ENTITIES : NO_ENTITIES;
  const tags = showEntities ? entityQuery.data?.tags ?? NO_TAGS : NO_TAGS;
  const suggestions = showEntities
    ? entityQuery.data?.suggestions ?? NO_ENTITIES
    : NO_ENTITIES;

  // Modules and Pages are categories in their own right, so they lead the chip
  // row rather than always occupying the top of the results list. Counts are
  // the raw match counts, never the gated ones — a chip has to keep showing
  // what it would bring back after you deselect it, exactly like the server's
  // facets, which ignore the entity-type filter for the same reason. A selected
  // category keeps its chip at zero so the filter can always be switched off.
  const facets = useMemo<SearchFacet[]>(() => {
    const navFacets: SearchFacet[] = [];
    if (moduleMatches.length > 0 || filters.entityTypes.includes(NAV_FACET_MODULES)) {
      navFacets.push({
        entity_type: NAV_FACET_MODULES,
        label: 'modules.title',
        icon: NAV_FACET_ICONS.modules,
        count: moduleMatches.length,
      });
    }
    if (pageMatches.length > 0 || filters.entityTypes.includes(NAV_FACET_PAGES)) {
      navFacets.push({
        entity_type: NAV_FACET_PAGES,
        label: 'globalSearch.pages',
        icon: NAV_FACET_ICONS.pages,
        count: pageMatches.length,
      });
    }
    return [...navFacets, ...(entityQuery.data?.facets ?? [])];
  }, [moduleMatches, pageMatches, filters.entityTypes, entityQuery.data?.facets]);

  // Everything the results list shows. `total_count` covers entities + tags
  // only — `searchApi` asks the server not to run its own page pass, since this
  // UI renders the richer, access-scoped navigation matches below instead.
  // The nav counts track the raw query while the entity count trails the 300ms
  // debounce, so a fast typist can briefly see the two halves out of step.
  const resultCount =
    (showEntities ? entityQuery.data?.total_count ?? 0 : 0) +
    moduleResults.length +
    pageResults.length;

  return {
    query,
    setQuery,
    filters,
    setFilters,
    moduleResults,
    pageResults,
    suggestedPages,
    entities,
    tags,
    suggestions,
    facets,
    entityQuery,
    resultCount,
    isLoading,
  };
}
